import type { EngineFactEnvelope, EngineId } from "../report/contracts";

const enginePriority: Readonly<Record<EngineId, number>> = {
  bazi: 0,
  ziwei: 1,
  iching: 2,
  feng_shui: 3,
  name_phone_wuxing: 4,
  pythagoras: 5,
};

export type FactConflict = Readonly<{
  factKey: string;
  preferredEngine: EngineId;
  values: readonly Readonly<{ engine: EngineId; value: unknown }>[];
}>;

function stableValueKey(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableValueKey).join(",")}]`;
  }

  if (value !== null && typeof value === "object") {
    return `{${Object.entries(value as Record<string, unknown>)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(
        ([key, nested]) => `${JSON.stringify(key)}:${stableValueKey(nested)}`,
      )
      .join(",")}}`;
  }

  return JSON.stringify(value);
}

export function rankEngineEvidence(
  envelopes: readonly EngineFactEnvelope[],
): readonly EngineFactEnvelope[] {
  return envelopes
    .map((envelope, originalIndex) => ({ envelope, originalIndex }))
    .sort(
      (left, right) =>
        enginePriority[left.envelope.engine] -
          enginePriority[right.envelope.engine] ||
        left.originalIndex - right.originalIndex,
    )
    .map(({ envelope }) => envelope);
}

export function detectFactConflicts(
  envelopes: readonly EngineFactEnvelope[],
): readonly FactConflict[] {
  const ranked = rankEngineEvidence(envelopes);
  const factKeys = Array.from(
    new Set(ranked.flatMap((envelope) => Object.keys(envelope.facts))),
  ).sort();

  return factKeys.flatMap((factKey) => {
    const values = ranked
      .filter((envelope) => Object.hasOwn(envelope.facts, factKey))
      .map((envelope) => ({
        engine: envelope.engine,
        value: envelope.facts[factKey],
      }));
    const distinctValues = new Set(
      values.map(({ value }) => stableValueKey(value)),
    );

    if (distinctValues.size <= 1) {
      return [];
    }

    return [
      {
        factKey,
        preferredEngine: values[0].engine,
        values,
      },
    ];
  });
}
